// Popover otevřený pod triggerem sekce. Portál do body, fixed pozicování z kotvy
// (stejný princip jako DropdownMenu), backdrop, drill-down sloupce, patička.

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/shared/ui/icon';

import { LoadBar } from './LoadIndicator';
import type { MenuLoadViz, MenuSection } from './menuTypes';
import { ColumnsView } from './views/ColumnsView';

export interface MenuPopoverProps {
  readonly section: MenuSection;
  readonly anchorRef: RefObject<HTMLButtonElement | null>;
  readonly loading: boolean;
  readonly loadViz: MenuLoadViz;
  readonly onClose: () => void;
}

export function MenuPopover({ section, anchorRef, loading, loadViz, onClose }: MenuPopoverProps) {
  const { t } = useTranslation('sharedUi');
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const ready = !loading;

  // Pozice + reflow při resize/scroll (princip z DropdownMenu).
  useLayoutEffect(() => {
    const update = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      if (!a) return;
      const w = menuRef.current?.offsetWidth ?? 300;
      let left = a.left;
      if (left + w > window.innerWidth - 6) left = Math.max(6, window.innerWidth - 6 - w);
      setPos({ top: a.bottom + 5, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRef]);

  // Escape zavírá (mimo-klik řeší backdrop).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const style: CSSProperties = {
    top: pos ? pos.top : -9999,
    left: pos ? pos.left : 0,
    visibility: pos ? 'visible' : 'hidden',
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[99999998]" onClick={onClose} />
      <div
        ref={menuRef}
        role="menu"
        className="bg-phx-surface border-phx-border-2 rounded-phx-md shadow-phx-lg text-phx-text fixed z-[99999999] overflow-hidden border"
        style={style}
      >
        <LoadBar viz={loadViz} ready={ready} />

        <ColumnsView section={section} loadViz={loadViz} ready={ready} />

        <div className="border-phx-border text-phx-text-muted mt-0.5 flex items-center gap-[7px] border-t px-3 pt-[7px] pb-1 text-[10px] whitespace-nowrap">
          <span>
            {t('mainMenu.clickPrefix')}{' '}
            <b className="text-phx-text-2 font-semibold">{t('mainMenu.openHint')}</b>
          </span>
          <span className="bg-phx-border-2 h-[11px] w-px shrink-0" />
          <span className="inline-flex items-center gap-1">
            <Icon name="detach" size={11} />
            <span>{t('mainMenu.detachHint')}</span>
          </span>
        </div>
      </div>
    </>,
    document.body
  );
}
